import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, AtSign, Lock, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import AuthCard from "@/components/jea/AuthCard";
import JeaInput from "@/components/jea/JeaInput";
import JeaButton from "@/components/jea/JeaButton";
import { useAuth } from "@/context/AuthContext";
import { apiClient, authApi, formatApiError } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

/** Phase 10F — Login page with two auth modes:
 *   • `password`  → classic password + 2FA (existing flow)
 *   • `passwordless` → request a 6-digit code to email/SMS, enter it, in.
 *
 * Both tabs share the same single identifier field. Backend auto-detects
 * email vs phone.
 */
const Tabs = ({ mode, setMode }) => (
  <div
    className="flex items-center bg-bg-tertiary border border-border-default rounded-[10px] p-1 gap-1"
    role="tablist"
    data-testid="login-tabs"
  >
    {[
      { key: "password", label: "Password", icon: Lock, testid: "login-tab-password" },
      { key: "passwordless", label: "Email / SMS Code", icon: KeyRound, testid: "login-tab-passwordless" },
    ].map(({ key, label, icon: Icon, testid }) => (
      <button
        key={key}
        type="button"
        role="tab"
        aria-selected={mode === key}
        onClick={() => setMode(key)}
        className={cn(
          "flex-1 h-10 inline-flex items-center justify-center gap-2",
          "rounded-[8px] text-[13px] font-semibold transition-all",
          mode === key
            ? "bg-success text-[#091424] shadow-[0_0_0_1px_rgba(46,204,113,0.4)]"
            : "text-text-secondary hover:text-white",
        )}
        data-testid={testid}
      >
        <Icon size={14} />
        {label}
      </button>
    ))}
  </div>
);

const PasswordForm = () => {
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      setError("Enter your email or phone number and password.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const trimmed = identifier.trim();
      const payload = { password };
      payload.identifier = trimmed.includes("@") ? trimmed.toLowerCase() : trimmed;
      const { data } = await apiClient.post("/auth/login", payload);
      nav("/2fa", {
        state: {
          pending_token: data.pending_token,
          email: data.email,
          available_channels: data.available_channels || ["email"],
          primary_channel: data.primary_channel || "email",
          auto_sent: data.auto_sent || null,
        },
      });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <JeaInput
        type="text"
        autoComplete="username"
        placeholder="Email or phone number"
        value={identifier}
        onChange={(e) => setIdentifier(e.target.value)}
        leftIcon={<AtSign size={16} />}
        data-testid="login-identifier-input"
      />
      <JeaInput
        type={show ? "text" : "password"}
        autoComplete="current-password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        leftIcon={<Lock size={16} />}
        data-testid="login-password-input"
        rightSlot={
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="p-2 rounded-md text-text-secondary hover:text-success transition-colors"
            aria-label={show ? "Hide password" : "Show password"}
            data-testid="login-toggle-password"
          >
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        }
      />
      {error && (
        <div
          className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2"
          data-testid="login-error"
        >
          {error}
        </div>
      )}
      <div className="pt-2">
        <JeaButton
          type="submit"
          size="lg"
          fullWidth
          loading={loading}
          data-testid="login-submit-button"
        >
          Log In
        </JeaButton>
      </div>
    </form>
  );
};

const PasswordlessForm = ({ switchToPassword }) => {
  const nav = useNavigate();
  const { completeAuth } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("request"); // "request" | "enter_code"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const normalizedIdentifier = () => {
    const t = identifier.trim();
    return t.includes("@") ? t.toLowerCase() : t;
  };

  const submitRequest = async (e) => {
    e?.preventDefault();
    setError("");
    if (!identifier.trim()) {
      setError("Enter your email or phone number.");
      return;
    }
    setLoading(true);
    try {
      await authApi.passwordlessRequestCode(normalizedIdentifier());
      setStage("enter_code");
      setCooldown(30);
      toast.success("If an account exists, a code is on its way.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setError("");
    if (code.length !== 6) {
      setError("Enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const data = await authApi.passwordlessVerifyCode(normalizedIdentifier(), code);
      completeAuth(data);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (stage === "request") {
    return (
      <form onSubmit={submitRequest} className="space-y-4" data-testid="passwordless-request-form">
        <JeaInput
          type="text"
          autoComplete="username"
          placeholder="Email or phone number"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          leftIcon={<Mail size={16} />}
          data-testid="passwordless-identifier-input"
        />
        <p className="text-[12px] text-text-secondary leading-relaxed">
          We'll send a one-time 6-digit code to your email or phone. No password required.
          The code is both your sign-in and your second factor.
        </p>
        {error && (
          <div
            className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2"
            data-testid="passwordless-error"
          >
            {error}
          </div>
        )}
        <JeaButton
          type="submit"
          size="lg"
          fullWidth
          loading={loading}
          data-testid="passwordless-request-button"
        >
          Send me a code
        </JeaButton>
      </form>
    );
  }

  return (
    <form onSubmit={submitVerify} className="space-y-4" data-testid="passwordless-verify-form">
      <div className="text-[13px] text-text-secondary">
        Code sent to <span className="text-white font-medium">{identifier}</span>. Enter it below to sign in.
      </div>
      <JeaInput
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="6-digit code"
        value={code}
        maxLength={6}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        leftIcon={<KeyRound size={16} />}
        data-testid="passwordless-code-input"
      />
      {error && (
        <div
          className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2"
          data-testid="passwordless-verify-error"
        >
          {error}
        </div>
      )}
      <JeaButton
        type="submit"
        size="lg"
        fullWidth
        loading={loading}
        data-testid="passwordless-verify-button"
      >
        Sign in
      </JeaButton>
      <div className="flex items-center justify-between text-[12px]">
        <button
          type="button"
          onClick={() => cooldown === 0 && submitRequest()}
          disabled={cooldown > 0 || loading}
          className={cn(
            "text-text-secondary hover:text-success underline-offset-2",
            cooldown > 0 ? "opacity-50 cursor-not-allowed" : "hover:underline",
          )}
          data-testid="passwordless-resend-button"
        >
          {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
        </button>
        <button
          type="button"
          onClick={switchToPassword}
          className="text-text-secondary hover:text-success hover:underline underline-offset-2"
          data-testid="passwordless-use-password-link"
        >
          Use password instead →
        </button>
      </div>
    </form>
  );
};

const LoginPage = () => {
  const [mode, setMode] = useState("password");
  return (
    <AuthCard>
      <div className="space-y-5">
        <Tabs mode={mode} setMode={setMode} />
        {mode === "password" ? <PasswordForm /> : (
          <PasswordlessForm switchToPassword={() => setMode("password")} />
        )}
        <div className="pt-4 flex flex-col items-center gap-2">
          <Link
            to="/forgot-password"
            className="text-[13px] text-text-secondary hover:text-success hover:underline transition-colors"
            data-testid="login-forgot-link"
          >
            Forgot Password
          </Link>
          <Link
            to="/register"
            className="text-[13px] font-semibold text-success hover:text-success/80 transition-colors"
            data-testid="login-create-account-link"
          >
            Create Account
          </Link>
        </div>
        <div className="mt-3 text-center">
          <Link
            to="/demo"
            className="text-[13px] font-medium text-success hover:text-success/80 transition-colors"
            data-testid="login-try-demo-link"
          >
            Try the demo →
          </Link>
        </div>
      </div>
    </AuthCard>
  );
};

export default LoginPage;
